import { useEffect, useState, useRef } from "react";
import {
    conversionAttempt,
    conversionFailure,
    conversionIntermediateFailure,
    conversionSuccess,
    formStart,
    pageView,
    mfaInlineError,
    mfaPageView,
    MfaInlineErrorData,
    TealiumLinkData
} from "../analytics";

export const usePageView = (pageName: string) => {
    useEffect(() => {
        pageView(pageName);
    }, [pageName]);
};

type MfaPageType = 'setup' | 'login' | 'error' | 'edit' | 'add' | 'enroll';
export const useMfaPageView = (type: MfaPageType, pageName: string) => {
    if (type === 'enroll') {
        type = 'setup';
    }

    useEffect(() => {
        mfaPageView(`my account: mfa: ${type}: ${pageName}`);
    }, [pageName, type]);
};

export const usePageViewWithSteps = (pageName: string, stepNum: number, stepName: string) => {
    useEffect(() => {
        pageView(pageName, { stepNum, stepName });
    }, [pageName, stepNum, stepName]);
};

/** used to mark "start" of a form (e.g. focusing into a form element) */
export const useFormStart = (flowName: string, isFirstStep = true) => {
    const [started, setStarted] = useState(false);

    useEffect(() => {
        if (started && isFirstStep) { // only fire form start on first page of multi step processes
            formStart(flowName);
        }
    }, [started, flowName, isFirstStep]);

    return () => setStarted(true);
};

export const useMfaInlineError = (fieldLabel: string) => {
    const sendErrEvent = (errorMessage: string, errorCode: string | null, errorType: MfaInlineErrorData['errorType']) => {
        mfaInlineError({ errorMessage, errorCode, fieldLabel, errorType });
    }
    return {
        validationError: (errMessage: string) => {
            sendErrEvent(errMessage, null, 'validation');
        },
        serverError: (errMessage: string, errCode: string) => {
            sendErrEvent(errMessage, errCode, 'system');
        }
    }
}

export const useConversion = (componentName: TealiumLinkData['component'], conversionName: TealiumLinkData['component']) => {

    const activeAttempt = useRef(false);

    const attempt = (coverageType?: TealiumLinkData['coverage_type']) => {
        conversionAttempt(conversionName, coverageType);
        activeAttempt.current = true;
    }

    const success = (coverageType?: TealiumLinkData['coverage_type']) => {
        if (!activeAttempt.current) {
            return;
        }
        conversionSuccess(componentName, coverageType);
        activeAttempt.current = false;
    }

    const fail = (errorType: string, errorMessage: string) => {
        conversionFailure(componentName, conversionName, errorType, errorMessage);
        activeAttempt.current = false;
    };

    const failIntermediate = (errorType: string, errorMessage: string) => {
        conversionIntermediateFailure(componentName, conversionName, errorType, errorMessage);
    };

    /** Fail validation at final step */
    const failValidation = (errorMessage: string) => {
        if (!activeAttempt.current) {
            return;
        }
        fail('validation', errorMessage);
    }

    /** Final validation before final step (multi-step views only) */
    const failValidationIntermediate = (errorMessage: string) => {
        if (!activeAttempt.current) {
            return;
        }
        failIntermediate('validation', errorMessage);
    }

    /** Fail API call at final step */
    const failAPI = (errorMessage: string, skipActiveAttemptCheck = false) => {
        if (!activeAttempt.current && !skipActiveAttemptCheck) {
            return;
        }
        fail('system', errorMessage);
    }

    /** Final API call before final step (multi-step views only) */
    const failAPIIntermediate = (errorMessage: string) => {
        if (!activeAttempt.current) {
            return;
        }
        failIntermediate('system', errorMessage);
    }

    return {
        attempt,
        success,
        failValidation,
        failValidationIntermediate,
        failAPI,
        failAPIIntermediate
    }
};